import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { lobbyService } from '../services/lobbyService';
import { GameHUD } from '../components/game/GameHUD';
import { useAuth } from '../context/AuthContext';
import { WordDisplay } from '../components/game/WordDisplay';
import { OutcomePanel } from '../components/game/OutcomePanel';
import { EndScreen } from '../components/game/EndScreen';
import { wordService } from '../services/wordService';
import { statsService } from '../services/statsService';
import { useSoundEngine } from '../hooks/useSoundEngine';
import './MultiplayerGame.css';

const TOTAL_TIME = 60;

const computeOutcome = (game, type, timeLeft) => {
  const isDouble = game.doubleActive === true;
  const currentWordObj = isDouble
    ? game.phraseWords?.[game.phraseWordIndex ?? 0]
    : game.words?.[game.currentWordIndex];
  const currentWordText = currentWordObj?.text ?? '';

  if (type === 'correct') {
    return isDouble
      ? {
          score: (game.score || 0) + 2,
          wordsGuessed: (game.wordsGuessed || 0) + 1,
          phraseWordIndex: (game.phraseWordIndex ?? 0) + 1,
          doubleActive: false,
          wordsHistory: [
            ...(game.wordsHistory || []),
            { word: currentWordText, result: 'correct', double: true },
          ],
        }
      : {
          score: (game.score || 0) + 1,
          wordsGuessed: (game.wordsGuessed || 0) + 1,
          currentWordIndex: (game.currentWordIndex || 0) + 1,
          wordsHistory: [
            ...(game.wordsHistory || []),
            { word: currentWordText, result: 'correct' },
          ],
        };
  }

  if (type === 'wrong') {
    return isDouble
      ? {
          score: Math.max(0, (game.score || 0) - 2),
          wrongAnswers: (game.wrongAnswers || 0) + 1,
          phraseWordIndex: (game.phraseWordIndex ?? 0) + 1,
          doubleActive: false,
          wordsHistory: [
            ...(game.wordsHistory || []),
            { word: currentWordText, result: 'wrong', double: true },
          ],
        }
      : {
          score: Math.max(0, (game.score || 0) - 1),
          wrongAnswers: (game.wrongAnswers || 0) + 1,
          currentWordIndex: (game.currentWordIndex || 0) + 1,
          wordsHistory: [...(game.wordsHistory || []), { word: currentWordText, result: 'wrong' }],
        };
  }

  if (type === 'pass') {
    if ((game.timerRemaining ?? timeLeft) <= 0) return {};
    return isDouble
      ? {
          phraseWordIndex: (game.phraseWordIndex ?? 0) + 1,
          doubleActive: false,
          wordsHistory: [
            ...(game.wordsHistory || []),
            { word: currentWordText, result: 'pass', double: true },
          ],
        }
      : {
          passesLeft: (game.passesLeft || 0) - 1,
          currentWordIndex: (game.currentWordIndex || 0) + 1,
          wordsHistory: [...(game.wordsHistory || []), { word: currentWordText, result: 'pass' }],
        };
  }

  return {};
};

const MultiplayerGame = () => {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const { currentUser } = useAuth();

  const code = navState?.code;
  const myId = navState?.myId ?? currentUser?.uid;
  const myRole = navState?.myRole;

  const [game, setGame] = useState(null);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [isRestarting, setIsRestarting] = useState(false);
  const rafRef = useRef(null);
  const interruptionStarted = useRef(false);
  const wasFinished = useRef(false);
  const countdownStartedRef = useRef(false);

  const [interruption, setInterruption] = useState(null);
  const [interruptionTimer, setInterruptionTimer] = useState(5);
  const [fastestWordMs, setFastestWordMs] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const prevGameStatus = useRef(null);
  const sound = useSoundEngine();

  // Subscribe to Firestore
  useEffect(() => {
    if (!code) {
      navigate('/multiplayer');
      return;
    }
    const unsub = lobbyService.subscribeToGame(code, (data) => {
      // If host triggered "Cambia Ruoli", redirect ALL clients back to lobby
      if (data.status === 'lobby' && wasFinished.current) {
        navigate('/multiplayer', { state: { rejoinCode: code, myId } });
        return;
      }
      setGame(data);
      if (data.status === 'finished' && !wasFinished.current) {
        wasFinished.current = true;
        confetti({
          particleCount: 140,
          spread: 100,
          origin: { y: 0.55 },
          colors: ['#FFCC00', '#FF8800', '#FF4433', '#22C55E'],
        });
        sound.playGameEnd();
      }
    });
    return () => unsub();
  }, [code, navigate, sound, myId]);

  // Timer sync from Firestore state
  useEffect(() => {
    if (!game) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (game.timerRunning && game.timerStartedAt) {
      const tick = () => {
        const elapsed = (Date.now() - game.timerStartedAt) / 1000;
        const remaining = Math.max(0, Math.ceil(game.timerRemaining - elapsed));
        setTimeLeft(remaining);

        if (remaining <= 0) {
          // Only host ends the game to avoid race conditions
          if (game.hostId === myId) {
            lobbyService.pauseGame(code, 0).catch(console.error);
          }
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      setTimeLeft(game.timerRemaining ?? TOTAL_TIME);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [game, code, myId, sound]);
  // Handle Interruption
  useEffect(() => {
    if (game?.status === 'interrupted' && !interruptionStarted.current) {
      interruptionStarted.current = true;
      setInterruption(game.interruptedBy || 'Un giocatore');
      setInterruptionTimer(5);

      const startTime = Date.now();
      let endSoundPlayed = false;

      const iv = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 5 - elapsed / 1000);
        setInterruptionTimer(remaining);

        if (remaining <= 0) {
          clearInterval(iv);
          if (!endSoundPlayed) {
            endSoundPlayed = true;
            sound.playInterruptionEnd();
          }
          if (code && myId) {
            lobbyService.leaveRoom(code, myId).catch(console.error);
          }
          navigate('/');
        }
      }, 50);

      return () => clearInterval(iv);
    }
  }, [game?.status, game?.interruptedBy, navigate, sound, code, myId]);

  // Countdown logic
  useEffect(() => {
    if (game?.status !== 'countdown') {
      // Reset ref when game is no longer in countdown status
      if (game?.status && game.status !== 'countdown') {
        countdownStartedRef.current = false;
      }
      return;
    }

    // Prevent multiple starts
    if (countdownStartedRef.current) return;
    countdownStartedRef.current = true;

    // Everyone plays the first beep
    setCountdown(3);
    sound.playCountdownBeep(3);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Only host triggers the actual game start in Firestore
          if (game.hostId === myId) {
            lobbyService.startGame(code).catch(console.error);
          }
          return prev;
        }
        const next = prev - 1;
        sound.playCountdownBeep(next);
        return next;
      });
    }, 900);

    return () => {
      clearInterval(interval);
    };
  }, [game?.status, game?.hostId, myId, code, sound]);

  // Track fastest word based on game state changes + suoni status
  useEffect(() => {
    const prev = prevGameStatus.current;
    const curr = game?.status;

    // Suono STOP — si attiva su tutti i dispositivi quando playing→paused
    if (curr === 'paused' && prev === 'playing') {
      sound.playStop();
      if (game.timerStartedAt && game.pausedAt) {
        const elapsed = game.pausedAt - game.timerStartedAt;
        if (fastestWordMs === null || elapsed < fastestWordMs) {
          setFastestWordMs(elapsed);
        }
      }
    }

    // Save stats when game ends
    if (curr === 'finished' && prev !== 'finished') {
      if (currentUser) {
        statsService
          .saveGameResult(currentUser.uid, {
            score: game.score || 0,
            wordsGuessed: game.wordsGuessed || 0,
            wrongAnswers: game.wrongAnswers || 0,
            passesUsed: 3 - (game.passesLeft || 0),
            fastestWordMs: fastestWordMs,
            wordsHistory: game.wordsHistory || [],
            mode: 'multiplayer',
          })
          .catch(console.error);
      }
    }

    prevGameStatus.current = curr;
  }, [
    game?.status,
    game?.timerStartedAt,
    game?.pausedAt,
    game?.score,
    game?.wordsGuessed,
    game?.wrongAnswers,
    game?.passesLeft,
    game?.wordsHistory,
    fastestWordMs,
    currentUser,
    sound,
  ]);

  const handleStop = useCallback(async () => {
    try {
      await lobbyService.pauseGame(code, timeLeft);
    } catch (err) {
      console.error('Failed to pause game:', err);
    }
  }, [code, timeLeft]);

  const handleOutcome = useCallback(
    async (type) => {
      if (!game) return;

      const isDouble = game.doubleActive === true;
      const currentWordObj = isDouble
        ? game.phraseWords?.[game.phraseWordIndex ?? 0]
        : game.words?.[game.currentWordIndex];
      const elapsedSec =
        game.timerStartedAt && game.pausedAt ? (game.pausedAt - game.timerStartedAt) / 1000 : 0;

      if (type === 'correct') {
        sound.playCorrect();
        if (currentWordObj?.id)
          wordService.updateWordStats(currentWordObj.id, 'correct', elapsedSec);
      } else if (type === 'wrong') {
        sound.playWrong();
        if (currentWordObj?.id) wordService.updateWordStats(currentWordObj.id, 'wrong');
      } else if (type === 'pass' && (game.timerRemaining ?? timeLeft) > 0) {
        sound.playNewWord();
        if (currentWordObj?.id) wordService.updateWordStats(currentWordObj.id, 'pass');
      } else if (type === 'report') {
        if (currentWordObj?.id) wordService.reportWord(currentWordObj.id);
        const refundTime = game.wordStartedAtRemaining ?? game.timerRemaining ?? timeLeft;
        const reportExtra = isDouble
          ? { phraseWordIndex: (game.phraseWordIndex ?? 0) + 1, doubleActive: false }
          : { currentWordIndex: (game.currentWordIndex || 0) + 1 };
        try {
          await lobbyService.waitForGuesser(code, refundTime, reportExtra);
        } catch (err) {
          console.error('Failed to report word:', err);
        }
        return;
      }

      const scoreUpdate = computeOutcome(game, type, timeLeft);
      // Flag _incrementDouble here so waitForGuesser can atomically increment the counter.
      // We cannot do it in guesserReady because doubleActive is already false by then.
      if (isDouble && (type === 'correct' || type === 'wrong' || type === 'pass')) {
        scoreUpdate._incrementDouble = true;
      }
      const isTimeUp = (game.timerRemaining ?? timeLeft) <= 0;

      try {
        if (type === 'cancel' && !isTimeUp) {
          await lobbyService.resumeGame(code, game.timerRemaining ?? timeLeft, {});
        } else if (isTimeUp || type === 'cancel') {
          const finalUpdate = { status: 'finished', timerRunning: false, ...scoreUpdate };
          if (isDouble) finalUpdate.doubleActive = false;
          // On time-up during a double, still increment doubleUsed
          if (finalUpdate._incrementDouble) {
            delete finalUpdate._incrementDouble;
            finalUpdate.doubleUsed = (game.doubleUsed ?? 0) + 1;
          }
          await lobbyService.updateGame(code, finalUpdate);
        } else {
          await lobbyService.waitForGuesser(code, game.timerRemaining ?? timeLeft, scoreUpdate);
        }
      } catch (err) {
        console.error('Failed to update game outcome:', err);
      }
    },
    [code, game, timeLeft, sound],
  );

  const handleGuesserReady = useCallback(async () => {
    // doubleActive is already false at this point (set by handleOutcome → waitForGuesser),
    // so we no longer pass _incrementDouble here — it's handled in waitForGuesser.
    try {
      await lobbyService.guesserReady(code, timeLeft, {});
    } catch (err) {
      console.error('Failed to mark guesser ready:', err);
    }
  }, [code, timeLeft]);

  const handleRestart = async () => {
    if (game.hostId !== myId) return;
    setIsRestarting(true);
    try {
      const [words, phraseWords] = await Promise.all([
        wordService.getWords(80),
        wordService.getPhraseWords(20),
      ]);
      await lobbyService.startCountdown(code, words, phraseWords);
    } catch (err) {
      console.error('Failed to restart:', err);
    }
    setIsRestarting(false);
  };

  const handleChangeRoles = async () => {
    // Only host calls this. returnToLobby sets status='lobby' in Firestore,
    // which triggers ALL clients (via subscription) to navigate to lobby.
    try {
      await lobbyService.returnToLobby(code);
    } catch (err) {
      console.error('Error returning to lobby:', err);
    }
    // Host navigates immediately (non-hosts are redirected by subscription)
    navigate('/multiplayer', { state: { rejoinCode: code, myId } });
  };

  const handleExit = async () => {
    const isFinished = game?.status === 'finished';
    if (!isFinished) {
      if (!window.confirm('Vuoi davvero abbandonare la partita?')) return;
    }

    try {
      if (code && myId) {
        const myName = game?.players?.find((p) => p.id === myId)?.name || 'Qualcuno';
        if (game?.status !== 'finished') {
          await lobbyService.interruptGame(code, myName);
        }
        await lobbyService.leaveRoom(code, myId);
      }
    } catch (err) {
      console.error('Error leaving room:', err);
    }
    navigate('/');
  };

  // Suono nuova parola (solo per i giver, quando currentWordIndex cambia durante playing)
  const prevMpWordIndex = useRef(-1);
  useEffect(() => {
    const isGiverRole = myRole === 'giver1' || myRole === 'giver2';
    if (!isGiverRole || !game) return;
    if (game.status === 'playing' && game.currentWordIndex !== prevMpWordIndex.current) {
      if (prevMpWordIndex.current !== -1) sound.playNewWord();
      prevMpWordIndex.current = game.currentWordIndex;
    }
    if (game?.status !== 'playing') prevMpWordIndex.current = -1;
  }, [game, myRole, sound]);

  if (!game) {
    return (
      <div className="mp-game mp-game--loading">
        <p>Connessione alla stanza…</p>
      </div>
    );
  }

  const isGiver = myRole === 'giver1' || myRole === 'giver2';
  const isGuesser = myRole === 'guesser';
  const isHost = game.hostId === myId;

  const isDouble = game.doubleActive === true;
  const doubleUsed = game.doubleUsed ?? 0;
  const score = game.score ?? 0;
  // Can double only if score >= 2, used < 2 and there are phrases left in the pool
  const canDouble =
    score >= 2 && doubleUsed < 2 && game.phraseWords?.length > (game.phraseWordIndex ?? 0);

  // Current word: phrase if double active, normal otherwise
  const currentWordObj = isDouble
    ? game.phraseWords?.[game.phraseWordIndex ?? 0]
    : game.words?.[game.currentWordIndex];
  const currentWordText = currentWordObj?.text ?? '…';

  // ── Finished ──────────────────────────────────────
  if (game.status === 'finished') {
    return (
      <div className="mp-game mp-game--end u-fade-in">
        <EndScreen
          score={game.score}
          wordsGuessed={game.wordsGuessed}
          wrongAnswers={game.wrongAnswers}
          passesUsed={3 - game.passesLeft}
          wordsHistory={game.wordsHistory || []}
          onRestart={handleRestart}
          isRestartLoading={isRestarting}
          onHome={handleExit}
          onChangeRoles={handleChangeRoles}
          isMultiplayer={true}
          isHost={isHost}
          allPlayersPresent={game?.players?.length === 3}
        />
      </div>
    );
  }

  return (
    <div className="mp-game">
      <GameHUD
        timeLeft={timeLeft}
        totalTime={TOTAL_TIME}
        score={game.score ?? 0}
        passesLeft={game.passesLeft ?? 3}
        onExit={handleExit}
      />

      <div className="mp-game__main">
        {/* LOBBY / idle waiting */}
        {game.status === 'lobby' && (
          <div className="mp-waiting">
            <p>In attesa che l'host avvii la partita…</p>
          </div>
        )}

        {/* PLAYING */}
        {game.status === 'playing' && (
          <>
            {isGiver ? (
              <WordDisplay word={currentWordText} animKey={game.currentWordIndex} />
            ) : null}

            {isGuesser ? (
              <button className="guesser-stop-btn" onClick={handleStop} id="btn-guesser-stop">
                STOP
              </button>
            ) : null}
          </>
        )}

        {/* COUNTDOWN */}
        {game.status === 'countdown' && (
          <div className="game-overlay">
            <span className="countdown-number" key={countdown}>
              {countdown}
            </span>
          </div>
        )}

        {/* PAUSED — outcome */}
        {game.status === 'paused' && (
          <>
            {isGiver ? (
              <OutcomePanel
                word={currentWordText}
                passesLeft={game.passesLeft ?? 0}
                onCorrect={() => handleOutcome('correct')}
                onWrong={() => handleOutcome('wrong')}
                onPass={() => handleOutcome('pass')}
                onReport={() => handleOutcome('report')}
                isTimeUp={timeLeft <= 0}
              />
            ) : null}
            {isGuesser ? (
              <div className="mp-waiting">
                <div className="mp-waiting__indicator">
                  <div className="pulse-circle"></div>
                  <div className="pulse-circle"></div>
                </div>
                <p>I suggeritori stanno scegliendo l'esito…</p>
                <span className="mp-waiting__sub">Attendi la prossima parola</span>
              </div>
            ) : null}
          </>
        )}

        {/* WAITING READY — guesser clicks Pronto */}
        {game.status === 'waiting_ready' && (
          <div className="mp-ready-screen">
            {isGuesser ? (
              <button
                className="guesser-ready-btn u-scale-in"
                onClick={handleGuesserReady}
                id="btn-guesser-ready"
              >
                PRONTO
              </button>
            ) : (
              <div className="mp-waiting">
                {canDouble ? (
                  <button
                    className={`double-btn${isDouble ? ' double-btn--active' : ''} u-scale-in`}
                    onClick={() => lobbyService.toggleDouble(code, !isDouble)}
                    id="btn-double"
                  >
                    <span className="double-btn__icon">⚡</span>
                    <span className="double-btn__text">RADDOPPIO</span>
                    <span className="double-btn__sub">
                      {isDouble ? 'ATTIVO' : 'INATTIVO'} · {2 - doubleUsed} rimasti
                    </span>
                  </button>
                ) : (
                  <div className="mp-waiting__indicator">
                    <div className="pulse-circle"></div>
                    <div className="pulse-circle"></div>
                  </div>
                )}
                <p>In attesa che il guesser sia pronto…</p>
                <span className="mp-waiting__sub">La prossima parola sta per arrivare</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Interruption Modal */}
      {interruption ? (
        <div className="game-overlay game-overlay--interruption">
          <div className="interruption-modal u-scale-in">
            <div className="interruption-modal__icon">⚠️</div>
            <h2 className="interruption-modal__title">Partita Interrotta</h2>
            <p className="interruption-modal__text">
              <strong>{interruption}</strong> ha lasciato la stanza...
            </p>
            <p className="interruption-modal__sub">Ritorno alla home tra poco</p>

            <div className="countdown-bar">
              <div
                className="countdown-bar__fill"
                style={{ width: `${(interruptionTimer / 5) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MultiplayerGame;
